
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SubscriptionPlan } from "@/lib/types";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SubscriptionPlansLoading } from "./components/SubscriptionPlansLoading";
import { SubscriptionPlanRow } from "./components/SubscriptionPlanRow";
import { formatCurrency } from "@/lib/formatters";

interface SubscriptionPlansTableProps {
  plans: SubscriptionPlan[];
  onDeletePlan: (planId: string) => void;
  isLoading?: boolean;
}

export function SubscriptionPlansTable({
  plans,
  onDeletePlan,
  isLoading = false,
}: SubscriptionPlansTableProps) {
  const navigate = useNavigate();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);

  const handleCreatePlan = () => {
    navigate("/admin/subscription-plans/new");
  };

  const handleEditPlan = (plan: SubscriptionPlan) => {
    navigate(`/admin/subscription-plans/${plan.id}/edit`);
  };

  const handleDelete = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedPlan) {
      onDeletePlan(selectedPlan.id);
      setIsDeleteDialogOpen(false);
      setSelectedPlan(null);
    }
  };

  const formatPrice = (price: number | null): string => {
    if (price === null || price === undefined) return 'Free';
    return formatCurrency(price, 'USD');
  };

  if (isLoading) {
    return <SubscriptionPlansLoading />;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Subscription Plans</h2>
        <Button onClick={handleCreatePlan}>New Plan</Button>
      </div>
      
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Monthly Price</TableHead>
              <TableHead>Annual Price</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Features</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plans.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-4 text-muted-foreground">
                  No subscription plans found
                </TableCell>
              </TableRow>
            ) : (
              plans.map((plan) => (
                <SubscriptionPlanRow 
                  key={plan.id}
                  plan={plan}
                  onEdit={() => handleEditPlan(plan)}
                  onDelete={handleDelete}
                  formatPrice={formatPrice}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the "{selectedPlan?.name}" plan.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsDeleteDialogOpen(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
